import { BackEndCodeLanguage, BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { Entity } from '@prophecy/shared-core/entity';
import {
  getIDEUrlPath,
  getSqlIDEUrlPath,
  Private_Routes as SharedPrivate_Routes,
  Public_Routes as SharedPublic_Routes,
  RouteMap,
  SQL_IDE_WORKSPACE_PATHS,
  VAPP_Private_Routes as SharedVAPP_Private_Routes,
  type UrlObject
} from '@prophecy/shared-core/url';
import { matchPath } from 'react-router-dom';

import { FINANCE_BASE, getBasePathFromUrl, METADATA_BASE, PROFESSIONAL_BASE } from './urlBasePath';

export * from '@prophecy/shared-core/url';

// Recursively wraps every `UrlObject` leaf (identified by having `getUrl`) with a
// `getUrlWithBasePath` method that prefixes the runtime base path. Shared cannot own
// this method — it reads `window.location` — so FE decorates the shared route trees.
type WithBasePath<T> = T extends { getUrl: (...args: infer A) => string }
  ? T & { getUrlWithBasePath: (...args: A) => string }
  : T extends object
    ? { [K in keyof T]: WithBasePath<T[K]> }
    : T;

function withBasePathDeep<T>(node: T): WithBasePath<T> {
  if (node && typeof node === 'object') {
    const asLeaf = node as unknown as UrlObject<string, unknown>;
    if (typeof asLeaf.getUrl === 'function') {
      // Absolute URLs (docs, support, external) don't want a base-path prefix — a leaf
      // like `Public_Routes.ProphecyDoc` returning `https://docs.prophecy.ai/` would
      // otherwise get `/metadata/https://…` when called via `getUrlWithBasePath`.
      const isExternal = typeof asLeaf.url === 'string' && /^https?:/.test(asLeaf.url);
      return {
        ...asLeaf,
        getUrlWithBasePath: (...args: unknown[]) => {
          const path = (asLeaf.getUrl as (...a: unknown[]) => string)(...args);
          return isExternal ? path : getBasePathFromUrl() + path;
        }
      } as WithBasePath<T>;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) out[k] = withBasePathDeep(v);
    return out as WithBasePath<T>;
  }
  return node as WithBasePath<T>;
}

export const Private_Routes = withBasePathDeep(SharedPrivate_Routes);
export const Public_Routes = withBasePathDeep(SharedPublic_Routes);
export const VAPP_Private_Routes = withBasePathDeep(SharedVAPP_Private_Routes);

type IDE_URL_PARAMS = Parameters<typeof SharedPrivate_Routes.IDE.getUrl>;
type SQL_IDE_URL_PARAMS = Parameters<typeof SharedPrivate_Routes.SQL_IDE.getUrl>;

export function getSqlIDEUrl({ uid, ...params }: SQL_IDE_URL_PARAMS['0'] & SQL_IDE_URL_PARAMS[1]) {
  return getSqlIDEUrlPath(getBasePathFromUrl(), uid, params);
}

function stripProductBasePath(pathname: string): string {
  const base = getBasePathFromUrl(pathname);
  if (!pathname.startsWith(base)) return pathname;
  const stripped = pathname.slice(base.length);
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

export function getSqlIdeUid(pathname: string): string | undefined {
  // `:uid` is the pattern's last segment, so a caller passing pathname+search (e.g. the
  // persisted "last visited IDE" URL) would otherwise have the query string captured as part
  // of the uid — strip it first so callers get the id, not id+query garbage.
  const normalizedPathname = stripProductBasePath(pathname.split('?')[0]);
  for (const path of SQL_IDE_WORKSPACE_PATHS) {
    const m = matchPath({ path, end: false }, normalizedPathname);
    if (typeof m?.params.uid === 'string') return m.params.uid;
  }
  return undefined;
}

/** True when navigating away from the SQL IDE URL for the given project id (same tab). */
export function isLeavingSqlProject(prevPathname: string, nextPathname: string, currentProjectUid: string): boolean {
  const prevUid = getSqlIdeUid(prevPathname);
  const nextUid = getSqlIdeUid(nextPathname);
  if (prevUid !== currentProjectUid) return false;
  return nextUid !== currentProjectUid;
}

export function getJobIdeUrl(
  jobId: string,
  projectId?: string,
  projectLanguage?: BackEndCodeLanguageType,
  branch?: string
) {
  if (!projectId) return '';
  return projectLanguage === BackEndCodeLanguage.sql
    ? getSqlIDEUrl({ uid: projectId, entity: Entity.Job.toLowerCase(), id: jobId, branch })
    : getIDEUrlPath(Entity.Job, jobId, { branch });
}

/** Opens SQL workspace using Finance vertical-app route (`/project/:uid`). */
export function getSqlProjectWorkspaceUrl(uid: string, query?: SQL_IDE_URL_PARAMS[1]) {
  return getSqlIDEUrlPath(getBasePathFromUrl(), uid, query);
}

/** Same URL shape as {@link getSqlIDEUrl}; routes to Finance project workspace. */
export function getSqlIDEUrlForProduct(args: SQL_IDE_URL_PARAMS['0'] & SQL_IDE_URL_PARAMS[1]) {
  const { uid, ...query } = args;
  return getSqlProjectWorkspaceUrl(uid, query);
}

export const getSignupUrl = (pathname: string) => {
  let base = METADATA_BASE;
  if (pathname.startsWith(FINANCE_BASE)) base = FINANCE_BASE;
  if (pathname.startsWith(PROFESSIONAL_BASE)) base = PROFESSIONAL_BASE;
  return base + RouteMap.SignUp;
};

// Re-typed IDE_URL_PARAMS shape kept in the export alias for consumers that grab it from url.ts.
export type { IDE_URL_PARAMS, SQL_IDE_URL_PARAMS };
